import { zodParse } from 'base/zod_parse';
import { computed, makeObservable, observable } from 'mobx';
import { z } from 'zod';
import {
  AnyDomainConfig,
  Connection,
  Domain,
  MutableOf,
  StateOf,
} from './base';
import { MIN_TIMESAMP } from './clock';
import { AnyDelta, DeltaType, ListDelta } from './deltas';

type OptionalConfig<I extends AnyDomainConfig> = {
  mutableBase: {
    replace(state: StateOf<I> | undefined): void;
    readonly current: MutableOf<I> | undefined;
  };
  state: Readonly<{
    // Ideally we'd define a getter & setter, so the state can just be the
    // inner state or undefined.
    // But typescript won't let us  have a different get & set type.  The mutable
    // needs to support setting to a state & getting as a mutable.
    current?: StateOf<I> | undefined;
  }>;
  delta: ListDelta<StateOf<I>>;
};

/**
 * Optional
 *
 * A domain which contains 0 or 1 of another domain.
 **/
export class OptionalDomain<I extends AnyDomainConfig>
  implements Domain<OptionalConfig<I>>
{
  constructor(readonly getInnerDomain: (() => Domain<I>) | Domain<I>) {}

  private get innerDomain(): Domain<I> {
    if (this.getInnerDomain instanceof Function) {
      return this.getInnerDomain();
    }
    return this.getInnerDomain;
  }

  create(
    ...args: Parameters<Domain<OptionalConfig<I>>['create']>
  ): MutableOf<OptionalConfig<I>> {
    return new OptionalMutable(...args, this.innerDomain);
  }

  getter(m: MutableOf<OptionalConfig<I>>) {
    return m;
  }

  statesEqual(a: StateOf<OptionalConfig<I>>, b: StateOf<OptionalConfig<I>>) {
    if (a.current == null && b.current == null) {
      return true;
    }
    if (a.current == null || b.current == null) {
      return false;
    }
    return this.innerDomain.statesEqual(a.current, b.current);
  }

  readonly stateSchema = z
    .object({
      current: z
        .lazy(() => this.innerDomain.stateSchema)
        .optional()
        .default(undefined),
    })
    .default({});

  isMutable(m: unknown): m is MutableOf<OptionalConfig<I>> {
    return m instanceof OptionalMutable && m.innerDomain === this.innerDomain;
  }
}

class OptionalMutable<I extends AnyDomainConfig>
  implements MutableOf<OptionalConfig<I>>
{
  readonly mutable = true;
  current: MutableOf<I> | undefined;

  private currentId = `${this.oid}.initial`;
  private currentTime = MIN_TIMESAMP;

  constructor(
    readonly oid: string,
    initialState: StateOf<OptionalConfig<I>>,
    private readonly cxn: Connection,
    readonly innerDomain: Domain<I>,
  ) {
    this.current =
      initialState.current != null
        ? this.innerDomain.create(this.currentId, initialState.current, cxn)
        : undefined;
    makeObservable(this, {
      current: observable.ref,
      state: computed,
      visibleOids: computed,
    });

    cxn.register(this, (d) => {
      if (d.type !== DeltaType.OPTIONAL_REPLACE) {
        throw new Error(`Unexpected delta ${d.type} for optional`);
      }
      if (d.time <= this.currentTime) {
        return; // outdated
      }
      // The time is used as the id.
      //   (We don't do this initially otherwise all items would have the same id)
      this.currentId = d.time;
      this.currentTime = d.time;
      this.current =
        d.to != null
          ? this.innerDomain.create(
              d.time,
              zodParse(innerDomain.stateSchema, d.to),
              cxn,
            )
          : undefined;
    });
  }

  get state(): StateOf<OptionalConfig<I>> {
    return { current: this.current?.state };
  }

  get visibleOids(): string[] {
    return this.current ? [this.oid, ...this.current.visibleOids] : [this.oid];
  }

  replace(state: StateOf<OptionalConfig<I>> | undefined) {
    if (state == null && this.current == null) return;
    if (
      state != null &&
      this.current != null &&
      this.innerDomain.statesEqual(this.current.state, state)
    )
      return;
    this.cxn.fromMutable({
      for: this.oid,
      time: this.cxn.clock.advance(),
      type: DeltaType.OPTIONAL_REPLACE,
      from: this.current?.state,
      to: state,
    });
  }

  wouldChange(delta: AnyDelta): boolean {
    return (
      delta.type === DeltaType.OPTIONAL_REPLACE && delta.time > this.currentTime
    );
  }
}
